import fetchApi from '../util/fetchApi';
import checkErrors from './checkErrors';

// Mirrors the backend InventoryScheduleDto (UweSapp.Application.Web/BL/Services/InventorySchedules).
// The AnalyticsDB is the source of truth; this API is the dedicated write/read path behind
// api/inventory-schedules (NOT the generic entity API).
export interface InventoryColumn {
  // What this column IS, stated outright rather than left to the backend to work out.
  //   'continue' — this column is the stored column named by `id`; its data follows, whatever the
  //                header now says. The column may be live (a keep or a rename) or retired (a revival).
  //   'new'      — mint a fresh column; `id` is ignored.
  // Required on save. The backend rejects a column that declares neither, and no longer infers.
  //
  // It used to infer, and that is the whole reason this field exists: identity was resolved by matching
  // the header, falling back to whatever `id` the editor had round-tripped for that grid row. Since the
  // id rode along invisibly through header edits, rewriting a layout in place silently rebound
  // data-bearing columns to headers that never meant them — CML lost 12 columns' provenance that way.
  binding?: 'continue' | 'new';
  // The AnalyticsDB VariableId of the column being continued. Read ONLY when binding === 'continue';
  // null for a new column. Meaningless across environments.
  id?: string | null;
  name: string;
  // 'number' (decimals) | 'integer' (whole numbers) | 'date' | 'text'.
  //
  // All unit-less: whether a column holds dollars or a rate is said by `format`, not by `type`. The
  // unit-bearing VariableTypes (AMOUNT, AMOUNT_UNITS, PERCENTAGE) belong to the financial schedules
  // and no inventory column can be declared as one. Note the names read crossed against the
  // VariableTypes behind them — 'number' is DECIMAL and 'integer' is NUMBER — see
  // TypeToVariableType.
  type: string;
  // How the download template PRESENTS the column, beyond what `type` already says:
  //   'None'                        — as the type implies (the default)
  //   'Decimal0' | 'Decimal2' | 'Decimal4'
  //                                 — display 0 / 0.00 / 0.0000; on the decimal-bearing types
  //   'DollarWhole' | 'DollarDecimal2' | 'PercentWhole' | 'PercentDecimal2'
  //                                 — $0 / $0.00 / 0% / 0.00%. These SUPPLY a unit, so a column
  //                                   holding money or a rate under a unit-less type renders as such
  //                                   without any type change at all — which is how an inventory
  //                                   column says it holds money, since no inventory type carries a
  //                                   unit.
  //   'DateIso' | 'DateUs' | 'DateIsoSlash'
  //                                 — yyyy-MM-dd | MM/dd/yyyy | yyyy/MM/dd; only on 'date'
  // For inventory, money and percentage are ONLY ever formats — never types.
  // The backend REJECTS a format its type cannot render, so offer only the usable ones (see
  // FORMATS_BY_TYPE in InventoryScheduleManager). Editing it is non-destructive: unlike a type change
  // the column keeps its id and its historical data, and nothing about storage changes — dates are
  // still stored canonically as yyyy-MM-dd whichever layout the template shows.
  //
  // On save this is AUTHORITATIVE: null/undefined mean 'None' and therefore CLEAR a format.
  // These are C# enum MEMBER NAMES (JsonStringEnumConverter), so the casing is exact.
  format?: string | null;
  order?: number;
  // Read-only: submitted inventory facts exist for this column (backend-populated). Drives the
  // "removing a column with live data" warning; ignored on upsert.
  hasData?: boolean;
  // The column's accepted values, in dropdown order — the "restricted values" configuration that the
  // download template turns into a dropdown. An ARRAY (even an empty one) means the column is restricted;
  // null/undefined means free text. Only 'text' columns may carry one; the backend rejects the rest.
  //
  // On save this is AUTHORITATIVE: whatever is sent replaces the stored set, and null clears it. Editing
  // it is non-destructive — unlike a type change, the column keeps its id and its historical data.
  acceptedValues?: string[] | null;
}

// Longest viewName the backend accepts (InventoryScheduleService.MAX_VIEW_NAME_LENGTH): Postgres's
// 63-char identifier limit minus 'ocif_analytics_' and the '_prelim' suffix of the preliminary twin.
export const MAX_VIEW_NAME_LENGTH = 63 - 'ocif_analytics_'.length - '_prelim'.length;

// A RETIRED column the generated view still shows, after every live one, under `name` (mirrors
// InventoryLegacyColumnDTO). For data only ever filed under a column the layout has since dropped.
export interface InventoryLegacyColumn {
  // The VariableId — the same identity a column binding uses. Never show it as identity.
  id: string;
  // Read-only portable token (e.g. "INV:CML:2").
  key?: string | null;
  // The column name in the view. Must differ from every live column and fit 63 bytes.
  name: string;
}

export interface InventorySchedule {
  fireId: string;
  scheduleType?: string | null;
  title: string;
  viewName?: string | null;
  columns: InventoryColumn[];
  // Historical columns. On save a list is AUTHORITATIVE (exactly these are shown); null leaves the stored
  // ones alone — which is what a restored layout sends, since a restore does not rewind them.
  legacyColumns?: InventoryLegacyColumn[] | null;
  // Read-only: submitted inventory facts exist for this schedule (incl. under retired columns).
  // Drives the "retiring a schedule with live data" warning; ignored on upsert.
  hasData?: boolean;
}

// A catalog entry: what the schedule IS called, not how it is laid out (mirrors InventoryScheduleSummaryDTO).
//
// This is what the lightweight `catalog()` returns, and it is a DIFFERENT type from InventorySchedule on
// purpose: the column layout is the expensive half of a full read (accepted values, declared types, and
// fact-table sweeps for the hasData flags), so callers that only need to name the schedules never fetch
// it — and can't mistake "not fetched" for "no columns".
export interface InventoryScheduleSummary {
  fireId: string;
  scheduleType?: string | null;
  title: string;
  viewName?: string | null;
  retired?: boolean;
}

// A stored column described so a person can recognise it (mirrors InventoryColumnFingerprintDTO).
//
// Bindings are only a real choice if the options are legible, and a column's actual identity — a
// VariableId, or a token like INV:CML:52bd19c513704c559df25caada582d64 — tells the reader nothing. So a
// column is characterised by what has been filed under it and what it has been called before.
//
// ONE ENTRY PER COLUMN (per Variable), not per stored row: a column can have one live row and any number
// of retired ones, and continuing it means continuing all of them.
export interface InventoryColumnFingerprint {
  // The value to post back as InventoryColumn.id when continuing this column. Never show it as identity.
  variableId: string;
  // The header it carries now, or bore when it was last live.
  currentHeader: string;
  // Every OTHER header its data has been filed under, most recent first. Empty for a column that has
  // only ever had one name — render nothing rather than an empty "formerly:" line.
  formerHeaders: string[];
  type: string; // 'text' | 'number' | 'integer' | 'date'
  // True when no live row binds it — off the current layout, but continuable (that is a revival).
  retired: boolean;
  retiredAt?: string | null;
  // The single most identifying number here.
  dataPoints: number;
  periods: number;
  firstPeriodLabel?: string | null;
  lastPeriodLabel?: string | null;
  institutions: number;
}

// A point this schedule's layout can be restored to (mirrors InventoryScheduleVersionDTO).
//
// Described the way a column binding is, and for the same reason: a bare timestamp is not something
// anyone can choose between. What identifies a restore point is the shape of the layout it holds and
// what restoring it would cost.
export interface InventoryScheduleVersion {
  // Pass back to `historyDefinition` to fetch what the schedule looked like at this moment.
  at: string;
  // UWE email of whoever saved it; null for versions no user authored — migrations, jobs, hand repairs.
  changedBy?: string | null;
  columns: number;
  columnHeaders: string[];
  // The version in force now. Restoring it is a no-op.
  isCurrent: boolean;
  // Relative to the CURRENT layout: columns that come back, get retired, get renamed back.
  restores: number;
  retires: number;
  renames: number;
}

// What a save WILL do to one column, as computed by the BACKEND from the binding it declared (mirrors
// InventoryColumnPlanDTO). Never re-derive these client-side — an editor-side approximation of the
// outcome is what let CML's layout rebind 12 data-bearing columns with no warning shown.
export interface InventoryColumnPlan {
  name: string;
  type: string;
  // Position the column will hold; -1 for a retirement.
  order: number;
  // keep   — continues a live column under the same header; nothing moves but the order.
  // revive — continues a RETIRED column under its own header; it comes back, and so does its data.
  // rename — continues a column under a DIFFERENT header, so its data follows. `fromHeader` is what
  //          those values mean today. The outcome worth reading twice; see `warning`.
  // new    — a fresh column; no data.
  // retire — (retirements only) a live column no posted column continues.
  // There is no 'retype': a column's value type is frozen once data exists, so changing it is refused
  // outright rather than silently minting a replacement. It arrives as 'new' plus a 'retire'.
  action: 'keep' | 'revive' | 'rename' | 'new' | 'retire';
  // Null only for 'new', where the column does not exist yet.
  variableId?: string | null;
  // For 'rename': the header the continued column carries today.
  fromHeader?: string | null;
  // Submitted facts on this column: what follows a rename, returns on a revive, or goes dark on a retire.
  dataPoints: number;
  warning?: 'CARRIES_UNRELATED_HISTORY' | 'HIDES_HISTORY' | null;
}

export interface InventorySchedulePlan {
  fireId: string;
  columns: InventoryColumnPlan[];
  // Live columns nothing continues. EXCLUDES a rename's source column — that is a retirement to the
  // executor but not to the reader, and listing it would count the same data twice under two headings.
  retirements: InventoryColumnPlan[];
  // True when anything here deserves an explicit confirmation. The editor gates the save on it.
  requiresConfirmation: boolean;
}

const BASE = 'inventory-schedules';

// The list is what the manager page reads with `useCache: true` (see `list` below), so a save or a
// retire has to drop it explicitly — otherwise the manager's own reload after writing would read back
// its pre-write self from the cache. Broad on purpose, like OrganizationalUnitApi's equivalent: every
// inventory-schedules URL (columns, history, the per-schedule read) is dropped too, since all of them
// describe state a save or retire can change.
function invalidateInventoryScheduleCache() {
  fetchApi.invalidateCache((url: string) => url.includes(BASE));
}

const InventoryScheduleApi = {
  // `useCache` lets the manager page's list view and its own edit view share one fetch instead of each
  // loading the catalog independently — see invalidateInventoryScheduleCache for how it stays fresh.
  async list(abortController?: AbortController, options?: { useCache?: boolean }): Promise<InventorySchedule[]> {
    return (await checkErrors(
      await fetchApi.api.get(BASE, { abortController, useCache: options?.useCache })
    )) as InventorySchedule[];
  },

  // Titles-only catalog — GET inventory-schedules/catalog. [Authorize]-only on the backend (which
  // schedules exist is not sensitive) and cheap: no column layout, no fact-table sweeps. Use this
  // anywhere a schedule just needs NAMING; `list` is the schedule-management read and needs
  // ManageObjectSchema.
  async catalog(abortController?: AbortController): Promise<InventoryScheduleSummary[]> {
    return (await checkErrors(
      await fetchApi.api.get(`${BASE}/catalog`, { abortController })
    )) as InventoryScheduleSummary[];
  },

  async get(fireId: string, abortController?: AbortController): Promise<InventorySchedule> {
    return (await checkErrors(
      await fetchApi.api.get(`${BASE}/${encodeURIComponent(fireId)}`, { abortController })
    )) as InventorySchedule;
  },

  // Whole-schedule upsert; returns the reconciled definition (with resolved column ids).
  async upsert(def: InventorySchedule): Promise<InventorySchedule> {
    const result = (await checkErrors(
      await fetchApi.api.put(BASE, { data: def })
    )) as InventorySchedule;
    invalidateInventoryScheduleCache();

    return result;
  },

  // Every stored column of the schedule — live AND retired — described by its filing record and its
  // former names. Fetch ONCE when a schedule is opened: it describes stored state, which the edit in
  // progress cannot change, and its period/institution aggregates are far too costly to re-run per
  // keystroke. Drives each row's binding line and the "which column does this continue?" picker.
  async columns(
    fireId: string,
    abortController?: AbortController
  ): Promise<InventoryColumnFingerprint[]> {
    return (await checkErrors(
      await fetchApi.api.get(`${BASE}/${encodeURIComponent(fireId)}/columns`, { abortController })
    )) as InventoryColumnFingerprint[];
  },

  // The points this schedule's layout can be restored to, newest first.
  async history(
    fireId: string,
    abortController?: AbortController
  ): Promise<InventoryScheduleVersion[]> {
    return (await checkErrors(
      await fetchApi.api.get(`${BASE}/${encodeURIComponent(fireId)}/history`, { abortController })
    )) as InventoryScheduleVersion[];
  },

  // The definition as it stood at `at`. This RESTORES NOTHING — it returns a body to load into the
  // editor, review, and save like any other edit. That is deliberate: restoring then goes through the
  // same preview and the same confirmation as hand editing, and is recorded as an ordinary new version
  // rather than as a special case that has to be kept in step with the normal save.
  //
  // Only the column layout is historical. Title, view name and accepted-value lists come from the
  // schedule as it stands now — none of them are versioned, and dropping the value lists would clear
  // every dropdown on save.
  async historyDefinition(
    fireId: string,
    at: string,
    abortController?: AbortController
  ): Promise<InventorySchedule> {
    return (await checkErrors(
      await fetchApi.api.get(
        `${BASE}/${encodeURIComponent(fireId)}/history/definition?at=${encodeURIComponent(at)}`,
        { abortController }
      )
    )) as InventorySchedule;
  },

  // DRY RUN of upsert: the same body, but nothing is written — returns what each column's fate would be
  // and how much data moves with it. Same validation as the PUT, so a body that previews cleanly saves.
  async preview(
    def: InventorySchedule,
    abortController?: AbortController
  ): Promise<InventorySchedulePlan> {
    return (await checkErrors(
      await fetchApi.api.post(`${BASE}/preview`, { data: def, abortController })
    )) as InventorySchedulePlan;
  },

  async remove(fireId: string): Promise<void> {
    // 204 No Content — opt out of json parsing and check the raw response.
    const res: Response = await fetchApi.api.delete(
      `${BASE}/${encodeURIComponent(fireId)}`,
      { json: false }
    );
    if (!res.ok) throw new Error(`Failed to delete schedule '${fireId}' (${res.status}).`);
    invalidateInventoryScheduleCache();
  },
};

export default InventoryScheduleApi;
